import React, { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import { UseFormReturn } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FileIcon, ReloadIcon } from '@radix-ui/react-icons'
import { UPLOAD_CUSTOMER_FILE } from '@/containers/B2BCustomers/B2BCustomersQueries'

// Must match the backend's MAX_CUSTOMER_FILE_BYTES and
// ALLOWED_CUSTOMER_FILE_EXTENSIONS (src/types-modified/CustomerFiles.ts);
// checking client-side too avoids streaming 120MB just to get a server
// rejection. Same binary-MB basis as the server (100 * 1024 * 1024 =
// 104857600) and the same strict-greater comparison, so the two agree to the
// byte.
export const MAX_CUSTOMER_FILE_BYTES = 100 * 1024 * 1024
// Derived so every piece of user-facing copy tracks the constant automatically
// and can never drift from it again.
export const MAX_CUSTOMER_FILE_MB = MAX_CUSTOMER_FILE_BYTES / (1024 * 1024)
export const ALLOWED_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx']

const formatBytes = (bytes?: number | null): string => {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface CustomerFileRowEditorProps {
  form: UseFormReturn<any>
  basePath: string
}

export function CustomerFileRowEditor({ form, basePath }: CustomerFileRowEditorProps) {
  const [uploadCustomerFile] = useMutation(UPLOAD_CUSTOMER_FILE)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const storageKey: string | null = form.watch(`${basePath}.storage_key`) ?? null
  const originalName: string | null = form.watch(`${basePath}.original_name`) ?? null
  const sizeBytes: number | null = form.watch(`${basePath}.size_bytes`) ?? null

  const uploadFile = useCallback(
    async (file: File) => {
      setError(null)
      const ext = (file.name.split('.').pop() ?? '').toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setError(`Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`)
        return
      }
      if (file.size > MAX_CUSTOMER_FILE_BYTES) {
        setError(
          `This file is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is ${MAX_CUSTOMER_FILE_MB} MB.`,
        )
        return
      }
      setIsUploading(true)
      try {
        const { data } = await uploadCustomerFile({ variables: { file } })
        const result = data?.uploadCustomerFile
        if (result) {
          const previousOriginal = form.getValues(`${basePath}.original_name`)
          const currentName = form.getValues(`${basePath}.name`)

          form.setValue(`${basePath}.storage_key`, result.storage_key, { shouldDirty: true })
          form.setValue(`${basePath}.original_name`, result.original_name, { shouldDirty: true })
          form.setValue(`${basePath}.mime_type`, result.mime_type, { shouldDirty: true })
          form.setValue(`${basePath}.size_bytes`, result.size_bytes, { shouldDirty: true })
          // Seed the label on first upload, and refresh it on replace unless the
          // admin has typed their own — never clobber a customised name.
          if (!currentName || currentName === previousOriginal) {
            form.setValue(`${basePath}.name`, result.original_name, { shouldDirty: true })
          }
        }
      } catch (e: any) {
        console.error('Customer file upload error:', e)
        setError(e?.message ?? 'Upload failed')
      } finally {
        setIsUploading(false)
      }
    },
    [basePath, form, uploadCustomerFile],
  )

  const handleFileInput = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) await uploadFile(file)
      // Reset so the same file can be re-selected after a validation error.
      event.target.value = ''
    },
    [uploadFile],
  )

  const inputId = `customer-file-input-${basePath.replace(/\./g, '-')}`

  // Per-row errors don't reach the section-level <FormMessage>, so surface
  // them here or an empty row silently blocks submit. Walk basePath rather
  // than assuming the parent array's name.
  const rowErrors = basePath
    .split('.')
    .reduce((acc: any, key) => acc?.[key], form.formState.errors as any)

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-sm font-medium">Display name</label>
        <Input
          placeholder="Name shown to the customer"
          {...form.register(`${basePath}.name`)}
        />
        <p className="text-xs text-muted-foreground">
          Shown in the B2B portal instead of the uploaded filename.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border-2 border-dashed border-gray-200 p-3">
        {storageKey ? (
          <div className="flex min-w-0 items-center gap-2">
            <FileIcon className="h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm">{originalName ?? storageKey.split('/').pop()}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(sizeBytes)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Upload a file ({ALLOWED_EXTENSIONS.join(', ')} — max{' '}
            {MAX_CUSTOMER_FILE_MB} MB)
          </p>
        )}
        <input
          type="file"
          id={inputId}
          accept={ALLOWED_EXTENSIONS.map(e => `.${e}`).join(',')}
          className="hidden"
          onChange={handleFileInput}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {isUploading ? (
            <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileIcon className="mr-2 h-4 w-4" />
          )}
          {isUploading ? 'Uploading…' : storageKey ? 'Replace' : 'Upload'}
        </Button>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
      {rowErrors?.storage_key && (
        <p className="text-xs text-destructive">
          {rowErrors.storage_key.message as string}
        </p>
      )}
      {rowErrors?.name && (
        <p className="text-xs text-destructive">
          {rowErrors.name.message as string}
        </p>
      )}
    </div>
  )
}
